/**
 * 地址显示工具函数
 * 用于处理地址的格式化显示和特殊地址的信息获取
 */

import {
	getSpecialAddressConfig,
	isSpecialAddress,
} from "@/lib/constants/special-addresses";

export interface SpecialAddressInfo {
	displayName: string;
	tag?: {
		label: string;
		variant: "default" | "canister" | "dev" | "official";
		className?: string;
	};
}

export const MARKED_NAME_CLASSNAME =
	"inline-flex max-w-full items-center rounded-[4px] border px-1.5 py-0.5";

export function getMarkedNameStyles(color: string) {
	return {
		color,
		borderColor: color,
	};
}

/**
 * 获取特殊地址的显示信息
 * @param address 地址
 * @param language 语言 ('zh' | 'en')
 * @returns 特殊地址信息，如果不是特殊地址则返回 null
 */
export function getSpecialAddressInfo(
	address: string,
	language: "zh" | "en",
): SpecialAddressInfo | null {
	if (!isSpecialAddress(address)) {
		return null;
	}

	const config = getSpecialAddressConfig(address);
	if (!config) return null;

	return {
		displayName: config.displayName[language],
		tag: config.tag
			? {
					label: config.tag.label[language],
					variant: config.tag.variant,
					className: config.tag.className,
				}
			: undefined,
	};
}

/**
 * 获取地址的显示名称
 * 如果是特殊地址，返回配置的显示名称；否则返回原地址
 * @param address 地址
 * @param language 语言 ('zh' | 'en')
 * @returns 显示名称
 */
export function getAddressDisplayName(
	address: string,
	language: "zh" | "en",
): string {
	const specialInfo = getSpecialAddressInfo(address, language);
	return specialInfo?.displayName || address;
}

/**
 * 格式化地址显示（截断显示）
 * @param address 地址
 * @param prefixLength 前缀长度
 * @param suffixLength 后缀长度
 * @returns 格式化后的地址
 */
export function formatAddress(
	address: string,
	prefixLength: number = 6,
	suffixLength: number = 6,
): string {
	if (address.length <= prefixLength + suffixLength) {
		return address;
	}
	return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}
